import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { Activity, Calendar as CalendarIcon, Filter, Search, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';

const ActivityLogs = () => {
  const { api } = useAuth();
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  // Filters
  const [selectedUser, setSelectedUser] = useState('all');
  const [selectedAction, setSelectedAction] = useState('all');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data.users);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const fetchLogs = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 25);
      if (selectedUser && selectedUser !== 'all') params.append('userId', selectedUser);
      if (selectedAction && selectedAction !== 'all') params.append('action', selectedAction);
      if (startDate) params.append('startDate', format(startDate, 'yyyy-MM-dd'));
      if (endDate) params.append('endDate', format(endDate, 'yyyy-MM-dd'));

      const res = await api.get(`/logs?${params.toString()}`);
      setLogs(res.data.logs);
      setPagination({
        page: res.data.page,
        pages: res.data.pages,
        total: res.data.total
      });
    } catch (err) {
      toast.error('Failed to fetch logs');
    }
    setLoading(false);
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    fetchLogs(1);
  };

  const clearFilters = () => {
    setSelectedUser('all');
    setSelectedAction('all');
    setStartDate(null);
    setEndDate(null);
    setTimeout(() => fetchLogs(1), 100);
  };

  const actionBadge = (action) => {
    const styles = {
      LOGIN: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
      LOGOUT: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
      START_RECORDING: 'bg-green-500/20 text-green-400 border-green-500/30',
      STOP_RECORDING: 'bg-red-500/20 text-red-400 border-red-500/30',
      CREATE_USER: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      UPDATE_USER: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      DELETE_USER: 'bg-red-500/20 text-red-400 border-red-500/30',
      DELETE_RECORDING: 'bg-red-500/20 text-red-400 border-red-500/30',
      UPDATE_SETTINGS: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30'
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-md border ${styles[action] || 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30'}`}>
        {action.replace(/_/g, ' ')}
      </span>
    );
  };

  const actionTypes = [
    'LOGIN', 'LOGOUT', 'START_RECORDING', 'STOP_RECORDING',
    'CREATE_USER', 'UPDATE_USER', 'DELETE_USER',
    'DELETE_RECORDING', 'UPDATE_SETTINGS'
  ];

  return (
    <div className="p-6 space-y-6" data-testid="activity-logs">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Activity Logs</h1>
          <p className="text-zinc-400 mt-1">System activity and audit trail</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="border-b border-zinc-800 pb-4">
          <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
            <Filter className="w-5 h-5 text-indigo-400" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">User</label>
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="log-user-filter">
                  <SelectValue placeholder="All users" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all">All users</SelectItem>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Action</label>
              <Select value={selectedAction} onValueChange={setSelectedAction}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="log-action-filter">
                  <SelectValue placeholder="All actions" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all">All actions</SelectItem>
                  {actionTypes.map(action => (
                    <SelectItem key={action} value={action}>{action.replace(/_/g, ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Start Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start bg-zinc-950 border-zinc-800 text-left font-normal" data-testid="log-start-date">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, 'PPP') : 'Pick date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800" align="start">
                  <Calendar mode="single" selected={startDate} onSelect={setStartDate} initialFocus />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <label className="text-sm text-zinc-400 mb-1 block">End Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start bg-zinc-950 border-zinc-800 text-left font-normal" data-testid="log-end-date">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, 'PPP') : 'Pick date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800" align="start">
                  <Calendar mode="single" selected={endDate} onSelect={setEndDate} initialFocus />
                </PopoverContent>
              </Popover>
            </div>

            <div className="flex items-end gap-2 lg:col-span-4">
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-500" data-testid="search-logs-btn">
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
              <Button type="button" variant="outline" onClick={clearFilters} className="border-zinc-700" data-testid="clear-log-filters-btn">
                <X className="w-4 h-4 mr-2" />
                Clear
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-400">Date & Time</TableHead>
                  <TableHead className="text-zinc-400">User</TableHead>
                  <TableHead className="text-zinc-400">Action</TableHead>
                  <TableHead className="text-zinc-400">Details</TableHead>
                  <TableHead className="text-zinc-400">Tracking ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8">
                      <div className="animate-spin w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-zinc-500">
                      No logs found
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => (
                    <TableRow key={log.id} className="border-zinc-800" data-testid={`log-row-${log.id}`}>
                      <TableCell className="text-zinc-400">{format(new Date(log.createdAt), 'PPp')}</TableCell>
                      <TableCell className="text-zinc-300">{log.userName}</TableCell>
                      <TableCell>{actionBadge(log.action)}</TableCell>
                      <TableCell className="text-zinc-400 max-w-xs truncate">{log.details || '—'}</TableCell>
                      <TableCell className="font-mono text-indigo-400">{log.trackingId || '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-800">
              <p className="text-sm text-zinc-400">
                Showing page {pagination.page} of {pagination.pages} ({pagination.total} total)
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fetchLogs(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="border-zinc-700"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fetchLogs(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="border-zinc-700"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ActivityLogs;
